import React, { useEffect, useState } from 'react';
import TeamSection from './TeamSection';
import TeamFoulsCounter from './TeamFoulsCounter';
import GameTimer from './GameTimer';
import PersonalPenalties from './PersonalPenalties';
import TeamInferiority from './TeamInferiority';
import ImageCarousel from './ImageCarousel';
import TimeoutOverlay from './TimeoutOverlay';
import BreakOverlay from './BreakOverlay';
import { useScoreboard } from './context/ScoreboardContext';
import { useLanguage } from './context/LanguageContext';
import { useDashboardLanguage } from './context/DashboardLanguageContext';
import { foulState } from '@rhs/core';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import { getWarningBackgroundColor } from './utils/colorUtils';

const ScoreboardLive = () => {
  // Listen for close message from opener
  useEffect(() => {
    function handleCloseMessage(event) {
      if (event.data && event.data.type === 'CLOSE_LIVE_VIEW') {
        // Vollbildmodus verlassen, falls aktiv
        if (document.fullscreenElement) {
          document.exitFullscreen().then(() => {
            window.close();
          }).catch(() => {
            // Fallback: Trotzdem versuchen zu schließen
            window.close();
          });
        } else if (document.webkitFullscreenElement) {
          document.webkitExitFullscreen();
          setTimeout(() => window.close(), 100);
        } else if (document.mozFullScreenElement) {
          document.mozCancelFullScreen();
          setTimeout(() => window.close(), 100);
        } else if (document.msFullscreenElement) {
          document.msExitFullscreen();
          setTimeout(() => window.close(), 100);
        } else {
          // Kein Vollbild aktiv, normal schließen
          window.close();
        }
      }
    }
    window.addEventListener('message', handleCloseMessage);
    return () => window.removeEventListener('message', handleCloseMessage);
  }, []);
  useGoogleAnalytics();
  const { t } = useLanguage();
  const { t: dt } = useDashboardLanguage();
  const {
    homeTeam,
    awayTeam,
    backgroundColor,
    activeTimeout,
    showTimeoutOverlay,
    carouselImages,
    homePenalties,
    awayPenalties,
    showTeamInferiority,
    homeInferiorityEntries,
    awayInferiorityEntries,
    calculateInferiorityTime,
    showBreakOverlay,
    period,
    isGameRunning,
    gameState,
    handleTimeoutEnd
  } = useScoreboard();

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const requestFullscreen = async () => {
    try {
      // Try to use the Screen Details API
      if ('getScreenDetails' in window) {
        console.log('Using Screen Details API');
        const screenDetails = await window.getScreenDetails();
        console.log('Available screens:', screenDetails.screens);
        
        // Get the current screen
        const currentScreen = screenDetails.screens.find(
          screen => screen.isPrimary
        );
        
        // Find a screen that isn't the primary screen
        const targetScreen = screenDetails.screens.find(
          screen => !screen.isPrimary
        );

        console.log('Current screen:', currentScreen);
        console.log('Target screen:', targetScreen);

        if (targetScreen) {
          // Move window to second screen
          window.moveTo(targetScreen.availLeft, targetScreen.availTop);
          
          // Longer delay to ensure window has fully moved
          await new Promise(resolve => setTimeout(resolve, 500));

          // Request fullscreen
          const element = document.documentElement;
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
          }
          return;
        }
      }
    } catch (error) {
      console.log('Screen Details API failed:', error);
    }

    // Fallback to basic window positioning if Screen Details API is not available
    // or if no second screen was found
    console.log('Using fallback positioning');
    window.moveTo(window.screen.availWidth, 0);
    
    // Longer delay to ensure window has fully moved
    await new Promise(resolve => setTimeout(resolve, 500));

    // Request fullscreen
    const element = document.documentElement;
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      await element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      await element.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const isWarningFoul = (fouls) => {
    return foulState(fouls).warning;
  };


  // Force a re-render every 100ms to ensure smooth updates
  const [, forceUpdate] = useState();
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate({});
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Calculate inferiority times from entries
  const homeTeamInferiorityTime = calculateInferiorityTime(homeInferiorityEntries);
  const awayTeamInferiorityTime = calculateInferiorityTime(awayInferiorityEntries);
  
  // Debug log
  console.log('[ScoreboardLive] Inferiority state:', {
    showTeamInferiority,
    homeEntriesLength: homeInferiorityEntries?.length,
    awayEntriesLength: awayInferiorityEntries?.length,
    homeEntries: homeInferiorityEntries,
    awayEntries: awayInferiorityEntries
  });
  
  // Show inferiority bar when there are entries (2+ penalties were active at some point)
  const shouldShowInferiorityBar = showTeamInferiority && 
    (homeInferiorityEntries.length > 0 || awayInferiorityEntries.length > 0);

  return (
    <div className="h-screen w-screen flex flex-col bg-white relative">
      {showTimeoutOverlay && (
        <TimeoutOverlay 
          team={activeTimeout?.team === 'home' ? homeTeam : awayTeam}
          timeLeft={activeTimeout?.timeLeft}
          isLiveView={true} 
          onTimeoutEnd={handleTimeoutEnd}
        />
      )}
      {showBreakOverlay && <BreakOverlay isLiveView={true} />}

      <div className="flex-1 grid grid-cols-[minmax(0,0.8fr),minmax(0,0.8fr),minmax(0,0.8fr)] items-start h-[55vh] min-h-[55vh] max-h-[55vh]">
        <TeamSection
          teamName={homeTeam.name}
          score={homeTeam.score}
          hasTimeout={homeTeam.hasTimeout}
          isLiveView={true}
          isWarningFoul={isWarningFoul(homeTeam.fouls)}
        />
        <div className="flex flex-col items-center justify-start h-full" style={{ backgroundColor }}>
          <GameTimer />
        </div>
        <TeamSection
          teamName={awayTeam.name}
          score={awayTeam.score}
          hasTimeout={awayTeam.hasTimeout}
          isLiveView={true}
          isWarningFoul={isWarningFoul(awayTeam.fouls)}
        />
      </div>
      
      <div className="grid grid-cols-[minmax(0,0.8fr),minmax(0,0.8fr),minmax(0,0.8fr)] h-[12vh] min-h-[12vh] max-h-[12vh]" style={{ backgroundColor }}>
        <div className={`flex items-center justify-center h-full w-full ${isWarningFoul(homeTeam.fouls) ? getWarningBackgroundColor(backgroundColor) : ''}`}>
          <TeamFoulsCounter 
            fouls={homeTeam.fouls}
            isLiveView={true}
            isWarningFoul={isWarningFoul(homeTeam.fouls)}
          />
        </div>
        <div className="flex flex-col items-center justify-center h-full">
          <div className="text-white text-[5vh] font-bold text-center">
            {dt('teamFouls')}
          </div>
        </div>
        <div className={`flex items-center justify-center h-full w-full ${isWarningFoul(awayTeam.fouls) ? getWarningBackgroundColor(backgroundColor) : ''}`}>
          <TeamFoulsCounter 
            fouls={awayTeam.fouls}
            isLiveView={true}
            isWarningFoul={isWarningFoul(awayTeam.fouls)}
          />
        </div>
      </div>

      {((homePenalties.length + awayPenalties.length) >= 1 || shouldShowInferiorityBar) && (  
      <div className="grid grid-cols-[minmax(0,0.8fr),minmax(0,0.8fr),minmax(0,0.8fr)] min-h-[10vh] max-h-[45vh]" style={{ backgroundColor }}>
        <div className="flex-1 flex flex-col items-center justify-center">
          <PersonalPenalties
            team="home"
            isLiveView={true}
          />
        </div>
        <div className="flex flex-col items-center justify-start h-full">
          <div className="text-white text-[5vh] font-bold text-center">
            {dt('penalties')}
            {showTeamInferiority && shouldShowInferiorityBar ? (
              <TeamInferiority isLiveView={true} />
            ) : null}
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <PersonalPenalties
            team="away"
            isLiveView={true}
          />
        </div>
      </div>
      )}

      {(showTeamInferiority && shouldShowInferiorityBar) ? (
        <div> </div>
      ) : (
        <>
          {(homePenalties.length === 0 && awayPenalties.length === 0) && (
            <div className="bg-white p-8 h-[32vh]">
              <ImageCarousel isLiveView={true} images={carouselImages} />
            </div>
          )}
          
          {((homePenalties.length === 1 && awayPenalties.length === 0) || (homePenalties.length === 0 && awayPenalties.length === 1)|| (awayPenalties.length === 1 && homePenalties.length === 1)) && (
            <div className="bg-white p-8 h-[22vh]">
              <ImageCarousel isLiveView={true} images={carouselImages} />
            </div>
          )}
          
          {(homePenalties.length >= 2 || awayPenalties.length >= 2) && (
            <div> </div>
          )}
        </>
      )}

      {!document.fullscreenElement && (
        <button
          onClick={requestFullscreen}
          className="absolute top-4 right-4 bg-amber-500 text-black px-6 py-3 rounded-lg hover:bg-amber-600 font-bold text-lg shadow-lg"
          title={t('liveView.fullScreenTooltip')}
        >
          🖥️ {t('liveView.fullScreenMode')}
        </button>
      )}
    </div>
  );
};

export default ScoreboardLive;
